import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/PageHeader";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";
import { BrazilDate } from "../../utils/BrazilDate";
import { useFilaIntegracaoEvents } from "../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../constants/rankingEventQueryKeys";
import {
  FilaPorcaoItem,
  filaPorcoesService,
} from "../../services/filaPorcoesService";
import { formatMesaSalaoLabel } from "../Painel/utils/origemPainel";

const statusLabel: Record<string, string> = {
  pending: "Na fila",
  done: "Finalizada",
  in_progress: "Na fila",
  cancelled: "Cancelada",
};

const statusClass: Record<string, string> = {
  pending: "bg-warning text-white",
  done: "bg-success text-white",
  in_progress: "bg-warning text-white",
  cancelled: "bg-error text-white",
};

const formatCreatedAt = (createdAt?: string | null) => {
  if (!createdAt) return "-";
  const date = new Date(String(createdAt));
  if (Number.isNaN(date.getTime())) return "-";
  return BrazilDate.formatBR(date, true);
};

const formatDescricao = (item: FilaPorcaoItem) => {
  const descricao = Array.isArray(item.descricao) ? item.descricao : [];
  if (descricao.length === 0) {
    const adicionais = Array.isArray(item.adicionais) ? item.adicionais : [];
    const nomes = adicionais
      .map((a) =>
        a.quantidade && a.quantidade > 1 ? `${a.nome} x${a.quantidade}` : a.nome
      )
      .join(", ");
    return nomes ? `${item.produtoNome} | Adicionais: ${nomes}` : item.produtoNome;
  }

  const [first] = descricao;
  const adicionais = first.adicionais?.length
    ? first.adicionais.join(", ")
    : "-";
  const extraCount = descricao.length > 1 ? ` +${descricao.length - 1}` : "";
  return `${first.name}${extraCount} | Adicionais: ${adicionais}`;
};

const FilaPorcoes: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>("pending");
  const [page, setPage] = useState(1);
  const todayISO = useMemo(() => BrazilDate.todayISO(), []);
  const itemsPerPage = 50;

  useFilaIntegracaoEvents(1, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.filaPorcoes,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["fila-porcoes", status, page, todayISO],
    queryFn: () =>
      filaPorcoesService.list({
        status: status || undefined,
        data: todayISO,
        page,
        limit: itemsPerPage,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ["fila-porcoes", "stats", todayISO],
    queryFn: () => filaPorcoesService.estatisticas(todayISO),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["fila-porcoes"] });
  };

  const finalizarMutation = useMutation({
    mutationFn: (id: number) => filaPorcoesService.finalizar(id),
    onSuccess: invalidate,
  });

  const filaAtiva = (stats?.pending ?? 0) + (stats?.inProgress ?? 0);

  const rows = data?.data ?? [];
  const tableData = rows.map((item) => {
    const descricaoText = formatDescricao(item);
    return [
      item.pedidoFoodfly?.comandaId?.trim()
        ? `Comanda #${item.pedidoFoodfly.comandaId.trim()}`
        : `#${item.numeroPedido}`,
      <div key={`p-${item.id}`} className="max-w-[360px]">
        <div className="truncate font-medium" title={descricaoText}>
          {descricaoText}
        </div>
        {item.observacao ? (
          <div
            className="truncate text-xs text-base-content/60"
            title={item.observacao}
          >
            Obs: {item.observacao}
          </div>
        ) : null}
      </div>,
      formatMesaSalaoLabel(
        item.pedidoFoodfly?.mesaId,
        item.pedidoFoodfly?.identificador,
      ) ||
        (item.pedidoFoodfly?.comandaId?.trim()
          ? `Comanda #${item.pedidoFoodfly.comandaId.trim()}`
          : "-"),
      <span
        key={`s-${item.id}`}
        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
          statusClass[item.status] || "bg-neutral text-white"
        }`}
      >
        {statusLabel[item.status] || item.status}
      </span>,
      formatCreatedAt(item.createdAt),
      <div key={`a-${item.id}`} className="flex flex-wrap gap-2">
        {item.status === "pending" || item.status === "in_progress" ? (
          <button
            type="button"
            className="btn btn-xs btn-success"
            disabled={finalizarMutation.isPending}
            onClick={() => finalizarMutation.mutate(item.id)}
          >
            Finalizar
          </button>
        ) : (
          <span className="text-xs text-base-content/50">—</span>
        )}
      </div>,
    ];
  });

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-4">
        <PageHeader
          title="Fila de Porções"
          description="Pedidos FoodFly — loja Birigui"
        />

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="rounded-full bg-warning/20 px-3 py-1">
            Na fila: <strong>{filaAtiva}</strong>
          </span>
          <span className="rounded-full bg-success/20 px-3 py-1">
            Finalizadas: <strong>{stats?.done ?? 0}</strong>
          </span>
          <select
            className="select select-bordered select-sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="pending">Na fila</option>
            <option value="done">Finalizadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          {(isLoading || isFetching) && (
            <span className="text-base-content/60">Atualizando…</span>
          )}
        </div>

        <Table
          headers={[
            "Pedido",
            "Descrição",
            "Mesa",
            "Status",
            "Criado",
            "Ações",
          ]}
          data={tableData}
        />

        {data && data.totalPages > 0 ? (
          <Pagination
            currentPage={data.page}
            totalPages={data.totalPages}
            totalItems={data.total}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
          />
        ) : null}
      </div>
    </main>
  );
};

export default FilaPorcoes;
